import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";

export const fetchAllMediaAsync = createAsyncThunk(
  "media/fetchAllMedia",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(`${BASE_URL}/v1/media`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const createMediaAsync = createAsyncThunk(
  "media/createMedia",
  async (mediaData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.post(
        `${BASE_URL}/v1/media`,
        mediaData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deleteMediaAsync = createAsyncThunk(
  "media/deleteMedia",
  async (mediaId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.delete(`${BASE_URL}/v1/media`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: [mediaId],
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updateMediaAsync = createAsyncThunk(
  "media/update",
  async (mediaData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/media`,
        mediaData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchMediaByAny = createAsyncThunk(
  "media/fetchByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(
        `${BASE_URL}/v1/media/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );


      return {
        data: response.data.data,
        total: response.data.data.length, // Include the total count
        isSingleMedia: key === "_id", // Flag to indicate if we're fetching by ID
      };
    } catch (error) {
      // If it's a 404 error (not found), return an empty array instead of rejecting
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSingleMedia: false };
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  isLoading: false,
  error: null,
  success: false,
  currentMedia: null,
  mediaList: [],
  fetchedMedia: [],
  total: 0, // Add total for pagination
};

const mediaSlice = createSlice({
  name: "media",
  initialState,
  reducers: {
    clearMediaState: (state) => {
      state.error = null;
      state.success = false;
      state.currentMedia = null;
    },
    loadCachedData: (state, action) => {
      return {
        ...state,
        mediaList: action.payload.mediaList || state.mediaList,
        fetchedMedia: action.payload.fetchedMedia || state.fetchedMedia,
        currentMedia: action.payload.currentMedia || state.currentMedia,
        total: action.payload.total || state.total,
        isLoading: false,
        error: null,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createMediaAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createMediaAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;

        // Get the new media data from the response
        const newMedia = action.payload.data;

        // Store as current media
        state.currentMedia = newMedia;

        // Add to mediaList array - at the beginning so it shows up first
        if (Array.isArray(state.mediaList)) {
          state.mediaList.unshift(newMedia);
        } else {
          state.mediaList = [newMedia];
        }

        if (Array.isArray(state.fetchedMedia)) {
          state.fetchedMedia.unshift(newMedia);
        } else {
          state.fetchedMedia = [newMedia];
        }

        // If we have a total count, increment it
        if (state.total !== undefined) {
          state.total += 1;
        }
      })
      .addCase(createMediaAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(fetchAllMediaAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllMediaAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.mediaList = action.payload.data;
      })
      .addCase(fetchAllMediaAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteMediaAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteMediaAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.mediaList = state.mediaList.filter(
          (media) => media._id !== action.meta.arg
        );
      })
      .addCase(deleteMediaAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(updateMediaAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateMediaAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;

        // Store the updated media data
        state.currentMedia = action.payload.data;

        const mediaId = action.payload.data._id;
        const mediaListIndex = state.mediaList.findIndex(
          (media) => media._id === mediaId
        );

        if (mediaListIndex !== -1) {
          state.mediaList[mediaListIndex] = action.payload.data;
        }

        if (state.fetchedMedia) {
          const fetchedMediaIndex = state.fetchedMedia.findIndex(
            (media) => media._id === mediaId
          );

          if (fetchedMediaIndex !== -1) {
            state.fetchedMedia[fetchedMediaIndex] = action.payload.data;
          }
        }
      })
      .addCase(updateMediaAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(fetchMediaByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMediaByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fetchedMedia = action.payload.data;
        state.total = action.payload.total;

        if (action.payload.isSingleMedia && action.payload.data.length > 0) {
          // If we're fetching by ID, also store as currentMedia
          state.currentMedia = action.payload.data[0];
        }
        state.error = null;
      })
      .addCase(fetchMediaByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const { clearMediaState, loadCachedData } = mediaSlice.actions;

// Selectors
export const selectMediaIsLoading = (state) => state.media.isLoading;
export const selectMediaError = (state) => state.media.error;
export const selectMediaSuccess = (state) => state.media.success;
export const selectCurrentMedia = (state) => state.media.currentMedia;
export const selectAllMedia = (state) => state.media.mediaList;
export const selectFetchedMedia = (state) => state.media.fetchedMedia || [];
export const selectMediaTotal = (state) => state.media.total || 0;
export const selectIsOfflineData = (state) =>
  state.media.isOfflineData || false;

export default mediaSlice.reducer;
